import { useState, useCallback } from "react"
import { useHistory } from "react-router-dom"
import Routes from "./Routes"
import type { CombinedUser } from "shared"
import {
	ConfigProvider,
	FeaturesProvider,
	useMountEffect,
	AppContextProvider,
	TanstackQueryProvider,
	onError,
	useLogin,
	AppSkeleton,
	araTheme,
} from "shared"
import { MantineProvider } from "@mantine/core"
import { TooltipProvider } from "shared/src/components/shadcn/components/ui/tooltip"
import { Toaster } from "shared/src/components/shadcn/components/ui/sonner"

import { Auth } from "@aws-amplify/auth"

// import CookieConsent from 'react-cookie-consent'
import { config } from "./config"

import ukLogo from "shared/src/images/uk-logo.svg"
import euLogo from "shared/src/images/eu-logo.svg"
import usLogo from "shared/src/images/us-logo.svg"
import apacLogo from "shared/src/images/apac-logo.svg"
import { useConfirmationDialog } from "shared/src/components/shadcn/components/molecules"

const nullCompany = { name: null, id: null }

const LOGOS = {
	"admin.hireara.ai": ukLogo,
	"admin.eu.hireara.ai": euLogo,
	"admin.us.hireara.ai": usLogo,
	"admin.apac.hireara.ai": apacLogo,
}

function AppMain() {
	const { showAlertInfo, DialogComponent } = useConfirmationDialog()
	const [user, setUser] = useState<CombinedUser>({ isAuthenticated: false, name: "" } as CombinedUser)
	const [company, setCompany] = useState(nullCompany)

	// Adapter to match the old DialogBox API signature
	const showAlert = useCallback(
		async (
			message: string | React.ReactNode,
			title: string | React.ReactNode,
			confirmButtonText?: string
		): Promise<boolean> => {
			await showAlertInfo(message, typeof title === "string" ? title : "Alert", confirmButtonText)
			return true
		},
		[showAlertInfo]
	)

	const performLogin = useLogin({
		responseOnly: true,
		setCompanyOverride: setCompany,
		setUserOverride: setUser,
		showAlert,
	})
	const history = useHistory()
	const [isAuthenticating, setIsAuthenticating] = useState(true)
	// const {width} = useWindowDimensions({includeHeight: false})
	const { logo, setLogoAndSendToDb, fetchLogoFromDb, sendLogoToDb } = {
		logo: LOGOS[window.location.hostname] || ukLogo,
		setLogoAndSendToDb: async () => {},
		fetchLogoFromDb: async () => {},
		sendLogoToDb: async () => {},
	}
	const navbarLinks = {
		Accounts: "/accounts",
		"Default Templates": "/defaulttemplates",
		"User Lookup": "/userlookup",
	}
	if (user.role === "admin") {
		navbarLinks["Settings"] = "/settings"
	}

	async function onLoad() {
		try {
			const response = await Auth.currentUserInfo()
			if (response !== null && response !== undefined) {
				// setUser(makeUser(response,{}))
				await performLogin({ response })
			}
		} catch (e) {
			if (e !== "No current user") {
				onError(e)
			}
		}
	}

	useMountEffect(async () => {
		await onLoad()
		setIsAuthenticating(false)
	})

	// useEffect(() => {
	// 	if (logo == null) {
	// 		fetchLogoFromDb(company)
	// 	}
	// }, [company])

	async function handleLogout() {
		await Auth.signOut()
		setUser({ isAuthenticated: false, name: "" } as CombinedUser)
		setCompany(nullCompany)
		history.push("/login")
	}

	function logoLink() {
		return user.isAuthenticated ? config.homepage : "/"
	}

	return (
		<>
			{DialogComponent}
			<TanstackQueryProvider>
				<ConfigProvider config={config}>
					<TooltipProvider delayDuration={250}>
						<FeaturesProvider company={company} user={user}>
							<MantineProvider theme={araTheme}>
								<AppSkeleton
									navbar={user.isAuthenticated}
									navbarDisabled={false}
									isAuthenticating={isAuthenticating}
									logoLink={logoLink()}
									navbarLinks={navbarLinks}
									userName={user.name}
									logo={logo}
									handleLogout={handleLogout}>
									<AppContextProvider
										value={{ user, setUser, company, setCompany }}
										logoValue={{ logo, setLogoAndSendToDb, fetchLogoFromDb, sendLogoToDb }}>
										<Routes />
									</AppContextProvider>
								</AppSkeleton>
							</MantineProvider>
						</FeaturesProvider>
					</TooltipProvider>
				</ConfigProvider>
				<Toaster />
			</TanstackQueryProvider>
		</>
	)
}

function App() {
	return <AppMain />
}

export default App
